import { useState } from "react";
import { vatSetting, unitPrices, penceText } from "../shared/agreementFields";

// ─── New customer, from the Agreements page ──────────────────────────────────
// In CamStorage a customer's details live on each unit they rent, so adding a customer means putting their
// details on one or more vacant units — or on a new unit added here. Afterwards the agreement form is filled in for them.

const isVacant = u => !u.tenant && !u.archived && !u.deleted_at;
const byId = (a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
const blankUnit = areas => ({ id: "", row_name: areas[0] || "", size: "", rent: "", vat_rent: "" });
const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

export function NewCustomerModal({ data, org, onSaveUnit, onAddUnit, onClose, onAdded }) {
  const vacant = data.filter(isVacant).sort(byId);
  const areas = [...new Set(data.filter(u => u.category === "Storage" && u.row_name).map(u => u.row_name))].sort();
  const [newUnits, setNewUnits] = useState(() => (vacant.length || !onAddUnit ? [] : [blankUnit(areas)]));
  const vat = vatSetting(org);
  const [form, setForm] = useState({ tenant: "", address: "", email: "", phone: "", move_in_date: todayIso(), status: "new", tenant_deposit: "" });
  const [units, setUnits] = useState({});   // id → { rent, vat_rent } for each ticked unit
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function toggle(u) {
    setUnits(s => {
      const next = { ...s };
      if (next[u.id]) delete next[u.id];
      else next[u.id] = { rent: u.rent ?? "", vat_rent: u.vat_rent ?? "" };
      return next;
    });
  }
  const setPrice = (id, k) => e => setUnits(s => ({ ...s, [id]: { ...s[id], [k]: e.target.value } }));
  const setNew = (i, k) => e => setNewUnits(list => list.map((u, j) => (j === i ? { ...u, [k]: e.target.value } : u)));
  const pricesOf = p => unitPrices({ rent: p.rent === "" ? null : p.rent, vat_rent: p.vat_rent === "" ? null : p.vat_rent }, vat);
  const priceLine = p => {
    const x = pricesOf(p);
    return x.inc !== null && x.ex !== null ? <span className="tsm tsub">{penceText(x.ex)} + VAT {penceText(x.vat)} = <strong>{penceText(x.inc)}</strong></span> : null;
  };

  async function save(e) {
    e.preventDefault();
    setError("");
    const ids = Object.keys(units);
    const fresh = newUnits.map(u => ({ ...u, id: u.id.trim() })).filter(u => u.id);
    if (form.tenant.trim().length < 2) { setError("Enter the customer's name"); return; }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError("That email address doesn't look right"); return; }
    if (newUnits.some(u => !u.id.trim() && (u.size || u.rent || u.vat_rent))) { setError("Give the new unit an ID, e.g. A12"); return; }
    const taken = fresh.find((u, i) => data.some(d => d.id === u.id) || fresh.findIndex(f => f.id === u.id) !== i);
    if (taken) { setError(`There's already a unit called "${taken.id}" — choose another ID`); return; }
    if (!ids.length && !fresh.length) { setError("Tick the unit(s) they're renting, or add a new unit"); return; }
    setBusy(true);
    const saved = [];
    const details = {
      tenant: form.tenant.trim(), address: form.address.trim() || null, email: form.email.trim() || null,
      phone: form.phone.trim() || null, move_in_date: form.move_in_date || null, status: form.status,
    };
    const single = ids.length + fresh.length === 1;
    for (const u of fresh) {
      const row = {
        id: u.id, label: null, category: "Storage", row_name: u.row_name || null, size: u.size.trim() || null, box_no: null,
        section: null, review: null, notes: null, payment: null, ...details,
        rent: u.rent === "" ? null : Number(u.rent), vat_rent: u.vat_rent === "" ? null : Number(u.vat_rent),
        tenant_deposit: single && form.tenant_deposit !== "" ? Number(form.tenant_deposit) : null,
      };
      if (await onAddUnit(row)) saved.push(row);
      else { setBusy(false); setError("Couldn't add that unit — see the message above."); if (saved.length) onAdded(saved); return; }
    }
    for (const id of ids) {
      const unit = data.find(u => u.id === id);
      const row = {
        ...unit, ...details,
        rent: units[id].rent === "" ? null : units[id].rent, vat_rent: units[id].vat_rent === "" ? null : units[id].vat_rent,
        tenant_deposit: single && form.tenant_deposit !== "" ? form.tenant_deposit : unit.tenant_deposit,
      };
      if (await onSaveUnit(row)) saved.push(row);
      else break;
    }
    setBusy(false);
    if (saved.length < ids.length + fresh.length) {
      setError(saved.length ? `Saved ${saved.map(r => r.id).join(", ")}, but the rest couldn't be saved — see the message above.` : "Couldn't save — see the message above.");
      if (saved.length) onAdded(saved);
      return;
    }
    onAdded(saved);
  }

  return (
    <div className="modal-ov" onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ width: 620 }}>
        <div className="mh">
          <div className="mt">New customer</div>
          <button className="mc" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <form onSubmit={save}>
          <div className="mb-m">
            <p className="tsub tsm" style={{ marginBottom: 12 }}>Their details are saved on the unit(s) they're renting, just like adding a tenant on the Site Plan. Then the agreement is filled in for them.</p>
            {error && <div className="login-err">{error}</div>}
            <div className="fg">
              <div className="fgi full"><label>Customer name</label><input value={form.tenant} onChange={set("tenant")} autoFocus /></div>
              <div className="fgi full"><label>Address</label><textarea rows={2} value={form.address} onChange={set("address")} placeholder="Their home or business address" /></div>
              <div className="fgi"><label>Email</label><input type="email" value={form.email} onChange={set("email")} /></div>
              <div className="fgi"><label>Phone</label><input value={form.phone} onChange={set("phone")} /></div>
              <div className="fgi"><label>Start / move-in date</label><input type="date" value={form.move_in_date} onChange={set("move_in_date")} /></div>
              <div className="fgi"><label>Status</label>
                <select value={form.status} onChange={set("status")}>
                  <option value="new">New Customer</option>
                  <option value="reserved">Reserved</option>
                  <option value="pending">Pending</option>
                  <option value="occupied">Occupied</option>
                </select></div>
            </div>

            <label className="tsm" style={{ fontWeight: 600, display: "block", margin: "14px 0 6px" }}>Unit(s) they're renting</label>
            {vacant.length === 0
              ? <div className="tsub tsm" style={{ marginBottom: 6 }}>All your units are taken, so add the unit they're renting below.</div>
              : (
                <div style={{ display: "grid", gap: 6, maxHeight: 260, overflowY: "auto" }}>
                  {vacant.map(u => {
                    const p = units[u.id];
                    return (
                      <div key={u.id} style={{ border: "1.5px solid #D8E2EE", borderRadius: 8, padding: "6px 10px", background: p ? "#EEF4FB" : "#fff" }}>
                        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
                          <input type="checkbox" checked={!!p} onChange={() => toggle(u)} />
                          <strong>{u.label || u.id}</strong>{u.size ? ` · ${u.size}` : ""}{u.row_name ? ` · ${u.row_name}` : ""}
                        </label>
                        {p && (
                          <div className="fr" style={{ gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <input type="number" step="0.01" min="0" value={p.rent} onChange={setPrice(u.id, "rent")} placeholder="Rent ex VAT £/mo" style={{ width: 150 }} />
                            <input type="number" step="0.01" min="0" value={p.vat_rent} onChange={setPrice(u.id, "vat_rent")} placeholder={vat?.registered ? `Inc VAT (auto at ${vat.rate}%)` : "Rent inc VAT £/mo"} style={{ width: 170 }} />
                            {priceLine(p)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            {newUnits.map((u, i) => (
              <div key={i} style={{ border: "1.5px dashed #C9D8E8", borderRadius: 8, padding: "8px 10px", marginTop: 6, background: "#F8FAFC" }}>
                <div className="fr" style={{ justifyContent: "space-between" }}>
                  <strong className="tsm">New unit</strong>
                  <button type="button" className="tsm" onClick={() => setNewUnits(list => list.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "var(--sub)", cursor: "pointer" }}>Remove</button>
                </div>
                <div className="fr" style={{ gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <input value={u.id} onChange={setNew(i, "id")} placeholder="Unit ID, e.g. A12" style={{ width: 130 }} />
                  {areas.length > 0 && (
                    <select value={u.row_name} onChange={setNew(i, "row_name")} style={{ width: 150 }}>
                      {areas.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  )}
                  <input value={u.size} onChange={setNew(i, "size")} placeholder="Size, e.g. 20ft" style={{ width: 120 }} />
                </div>
                <div className="fr" style={{ gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <input type="number" step="0.01" min="0" value={u.rent} onChange={setNew(i, "rent")} placeholder="Rent ex VAT £/mo" style={{ width: 150 }} />
                  <input type="number" step="0.01" min="0" value={u.vat_rent} onChange={setNew(i, "vat_rent")} placeholder={vat?.registered ? `Inc VAT (auto at ${vat.rate}%)` : "Rent inc VAT £/mo"} style={{ width: 170 }} />
                  {priceLine(u)}
                </div>
              </div>
            ))}
            {onAddUnit && <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => setNewUnits(list => [...list, blankUnit(areas)])}>+ Add a new unit</button>}
            {Object.keys(units).length + newUnits.length === 1 && (
              <div className="fg" style={{ marginTop: 10 }}>
                <div className="fgi"><label>Deposit held £ (optional)</label><input type="number" step="0.01" min="0" value={form.tenant_deposit} onChange={set("tenant_deposit")} /></div>
              </div>
            )}
          </div>
          <div className="mf">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Save customer & continue"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
